import { type ReactElement, useState } from 'react'
import { AvatarBlurhash, type AvatarProps } from 'app/components/AvatarBlurhash'
import type { TeamId } from '@terros/common'
import { showNotification } from '@mantine/notifications'
import { Box, FileButton, Loader, Tooltip, UnstyledButton } from '@mantine/core'
import { basicS3Upload, SizePrimitives, useTeamCache, useTranslation } from '@front/model'
import { MaterialSymbolIcon } from '../terros/icon/MaterialSymbolIcon'

type UpdateTeamAvatarButtonProps = {
  teamId: TeamId
  uploadAvatarUrl: string | undefined
  disabled?: boolean
} & AvatarProps

export const UpdateTeamAvatarButton = ({
  teamId,
  uploadAvatarUrl,
  disabled,
  size = 'md',
  ...avatarProps
}: UpdateTeamAvatarButtonProps): ReactElement => {
  const { t } = useTranslation()
  const cache = useTeamCache()
  const [isUploading, setIsUploading] = useState(false)

  const uploadAvatar = async (file: File | null): Promise<void> => {
    if (!file) return
    try {
      setIsUploading(true)
      await basicS3Upload(uploadAvatarUrl, file)
      // give backend resize worker a moment to finish
      setTimeout(() => cache.updateAvatar(teamId), 2000)
      showNotification({
        message: t('Photo Successfully Changed'),
        color: 'green',
      })
    } catch {
      showNotification({
        message: t('Photo Update Failed'),
        color: 'red',
      })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <FileButton accept='image/png,image/jpeg' disabled={disabled || isUploading} onChange={uploadAvatar}>
      {(props) => (
        <Tooltip label={t('Change Photo')}>
          <UnstyledButton aria-label={t('Change Photo')} {...props} disabled={disabled || isUploading} type='button'>
            <Box pos='relative'>
              <AvatarBlurhash {...avatarProps} size={size} />
              {isUploading && <Loader pos='absolute' size='sm' top='calc(50% - 9px)' left='calc(50% - 9px)' />}
              {!disabled && !isUploading && <CameraBadge />}
            </Box>
          </UnstyledButton>
        </Tooltip>
      )}
    </FileButton>
  )
}

const CameraBadge = (): ReactElement => (
  <Box
    bg='var(--mantine-color-brand-filled)'
    bottom={-SizePrimitives['1']}
    className='pointer-events-none flex items-center justify-center rounded-full border border-[var(--mantine-color-body)]'
    h='md'
    pos='absolute'
    right={-SizePrimitives['1']}
    w='md'
  >
    <MaterialSymbolIcon color='var(--mantine-color-white)' icon='photo_camera' size='sm' />
  </Box>
)
